/**
 * AdminPageLayout - 공통 관리자 페이지 레이아웃
 * 모든 관리자 페이지에 일관된 레이아웃과 스타일을 제공
 */

import { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

interface AdminPageLayoutProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  loading?: boolean;
  error?: string | null;
  children: ReactNode;
  headerActions?: ReactNode;
}

export default function AdminPageLayout({
  title,
  description,
  icon,
  loading = false,
  error = null,
  children,
  headerActions,
}: AdminPageLayoutProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{title}</h1>
          {description && <p className="text-muted-foreground mt-1">{description}</p>}
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            {icon && <div className="text-primary">{icon}</div>}
            <h1 className="text-3xl font-bold">{title}</h1>
          </div>
          {description && (
            <p className="text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        {headerActions && <div className="flex gap-2">{headerActions}</div>}
      </div>

      {/* 페이지 컨텐츠 */}
      {children}
    </div>
  );
}

